import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Descriptions, Input, Modal, Select, Table, Tag } from "antd";
import Cookies from "js-cookie";

const API = "https://www.smart-lend.com/api/support/property-application-drafts";
const date = (value) => value ? new Date(value).toLocaleString("en-SG") : "—";
const label = (key) => ({
  oustandingLoan: "Outstanding loan", cpfUsage: "CPF usage", cbsFiles: "CBS files selected",
  cbsExtractedData: "CBS extracted details", agreed: "Terms accepted",
  documentForLoanOustanding: "Outstanding loan documents", documentForCpfUsage: "CPF documents",
  selectedLoanType: "Selected loan type (0: equity term, 1: bridging, 2: half caveat)",
  loanTenureMonths: "Selected tenure in months",
  estimatedPropertyValue: "Estimated full property value",
}[key] || key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " "));

function SavedValues({ value }) {
  if (value === null || value === undefined || value === "") return <span>—</span>;
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (typeof value !== "object") return <span style={{ overflowWrap: "anywhere" }}>{String(value)}</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span>None</span>;
    return <div>{value.map((item, i) => <div key={i} style={{ marginBottom: 8 }}><SavedValues value={item} /></div>)}</div>;
  }
  return <Descriptions size="small" bordered column={1}>
    {Object.entries(value).filter(([key]) => key !== "loanRepaymentSchedule").map(([key, item]) => (
      <Descriptions.Item key={key} label={label(key)}><SavedValues value={item} /></Descriptions.Item>
    ))}
  </Descriptions>;
}

const address = (row) => {
  const fields = row.snapshot?.form?.property?.address;
  if (!fields) return "No property selected";
  return ["block", "street", "floor", "unit", "postal"].map((key) => fields[key]?.value).filter(Boolean).join(" ");
};
const state = (row) => row.submitted_at ? "Submitted" :
  Date.now() - new Date(row.last_activity_at).getTime() > 30 * 60 * 1000 ? "Inactive" : "In progress";

async function get(url) {
  const response = await fetch(url, { headers: {
    Authorization: `Bearer ${Cookies.get("support_token")}`, Accept: "application/json",
  } });
  if (!response.ok) throw new Error("Unable to load application progress. Please retry.");
  return response.json();
}

export default function PropertyApplicationDrafts({ onOpenBorrower }) {
  const [status, setStatus] = useState("incomplete");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState({ data: [], total: 0 });
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const result = await get(`${API}?${new URLSearchParams({ status, search, page })}`);
      if (id === requestId.current) { setData(result); setError(""); }
    } catch (err) { if (id === requestId.current) setError(err.message); }
    finally { if (id === requestId.current) setLoading(false); }
  }, [status, search, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError("");
    get(`${API}/${selected.id}/history?page=${historyPage}`).then((result) => {
      if (!cancelled) setHistory(result);
    }).catch((err) => { if (!cancelled) setHistoryError(err.message); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [selected, historyPage]);
  return <section style={{ background: "white", padding: 20, borderRadius: 16 }}>
    <h2>Property application follow-up</h2>
    <p>Saved applications from the property term loan page. Inactive means no saved activity for 30 minutes; it does not confirm the borrower has abandoned the application.</p>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
      <Select aria-label="Application status" value={status} style={{ minWidth: 220 }} onChange={(value) => { setStatus(value); setPage(1); }} options={[
        { value: "incomplete", label: "All incomplete applications" },
        { value: "inactive", label: "Inactive for 30+ minutes" },
        { value: "submitted", label: "Submitted applications" }, { value: "all", label: "All applications" },
      ]} />
      <Input.Search placeholder="Search borrower name, email or phone" allowClear style={{ maxWidth: 350 }} onSearch={(value) => { setSearch(value); setPage(1); }} />
      <Button onClick={load} loading={loading}>Refresh</Button>
    </div>
    {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
    <Table rowKey="id" loading={loading} dataSource={data.data} scroll={{ x: 1050 }}
      pagination={{ current: page, total: data.total, pageSize: 25, showSizeChanger: false, onChange: setPage }} columns={[
        { title: "Borrower / contact", key: "borrower", render: (_, row) => <div>
          <Button type="link" style={{ padding: 0 }} disabled={!row.borrower} onClick={() => onOpenBorrower(row.borrower)}>{row.borrower?.principal_name || `Borrower ${row.user_id}`}</Button>
          <div>{row.borrower?.mobile_no || row.borrower?.company_contact || "—"}</div>
          <div>{row.borrower?.email || row.borrower?.company_email || "—"}</div>
        </div> },
        { title: "Property", key: "property", render: (_, row) => address(row) },
        { title: "Loan amount", key: "amount", render: (_, row) => row.snapshot?.form?.loanAmount ? `S$${Number(row.snapshot.form.loanAmount).toLocaleString("en-SG")}` : "Not entered" },
        { title: "Last step", dataIndex: "step" },
        { title: "Status", key: "status", render: (_, row) => <Tag color={row.submitted_at ? "green" : state(row) === "Inactive" ? "orange" : "blue"}>{state(row)}</Tag> },
        { title: "Last activity", dataIndex: "last_activity_at", render: date },
        { title: "Details", key: "details", render: (_, row) => <Button onClick={() => { setSelected(row); setHistory({ data: [], total: 0 }); setHistoryPage(1); }}>View progress</Button> },
      ]} />
    <Modal open={!!selected} onCancel={() => setSelected(null)} footer={null} width={950} title="Saved property application">
      {selected && <>
        <p>Started: {date(selected.created_at)} · Last saved: {date(selected.last_activity_at)}</p>
        {selected.submitted_at && <Alert type="success" message={`Submitted as property loan #${selected.prop_term_request_id} on ${date(selected.submitted_at)}`} />}
        <h3>Latest application details</h3>
        <SavedValues value={selected.snapshot} />
        <h3>Progress history</h3>
        <p>Expand a saved update to inspect the values at that stage.</p>
        {historyError && <Alert type="error" message={historyError} />}
        <Table rowKey="id" size="small" loading={historyLoading} dataSource={history.data}
          columns={[{ title: "Saved at", dataIndex: "created_at", render: date }, { title: "Step", dataIndex: "step" }]}
          expandable={{ expandedRowRender: (event) => <SavedValues value={event.snapshot} /> }}
          pagination={{ current: historyPage, total: history.total, pageSize: 20, showSizeChanger: false, onChange: setHistoryPage }} />
      </>}
    </Modal>
  </section>;
}
